import {
  useRevisionCreatedSubscription,
  useRevisionDeletedSubscription,
  useRevisionMovedSubscription,
  RevisionDeletedFragment,
  RevisionInfoFragment,
} from 'gqlTypes'
import { ErrorHandler } from 'modules/tracing'
import { updateActivityFeed } from './updateActivityFeed'

const SUBSCRIPTIONS_SHOULD_BE_VALID = (name: string) =>
  `Received "${name}" subscription data should always be valid one`

export interface RevisionMovedProps {
  type: 'revision-moved'
  revision: RevisionInfoFragment
  selectedRevisionChange: boolean
}

export interface RevisionDeletedProps {
  type: 'revision-deleted'
  revision: RevisionDeletedFragment
  selectedRevisionChange: boolean
}

export interface RevisionCreatedProps {
  type: 'revision-created'
  revision: RevisionInfoFragment
  selectedRevisionChange: false
}

export type RevisionChangedProps =
  | RevisionMovedProps
  | RevisionDeletedProps
  | RevisionCreatedProps

export interface UseRevisionSubscriptionsProps {
  artboard: {
    permanentArtboardShortId: string
    identifier: string
  }
  share: {
    identifier: string
    userCanAccessVersions: boolean
  }
  onRevisionChange: (props: RevisionChangedProps) => void
}

export const useRevisionSubscriptions = ({
  artboard: { permanentArtboardShortId, identifier: artboardIdentifier },
  share,
  onRevisionChange,
}: UseRevisionSubscriptionsProps) => {
  const variables = {
    permanentArtboardShortId,
    includeArtboardDetails: share.userCanAccessVersions,
  }

  // New created revisions can appear only as the most recent activity event
  // therefore we just need to add it the beginning of the list
  useRevisionCreatedSubscription({
    variables,
    onSubscriptionData: ({
      client,
      subscriptionData: { loading, data, error },
    }) => {
      if (!data || error || loading) {
        ErrorHandler.shouldNeverHappen(
          SUBSCRIPTIONS_SHOULD_BE_VALID('revisionCreated')
        )
        return
      }

      onRevisionChange({
        type: 'revision-created',
        selectedRevisionChange: false,
        revision: data.revisionCreated,
      })

      // Try to update activity feed, if the user went directly to the
      // inspector this is expected to fail
      updateActivityFeed({
        client,
        variables: {
          ...variables,
          shareIdentifier: share.identifier,
        },
        update: activityFeed => {
          activityFeed.meta.totalCount++
          activityFeed.entries.unshift(data.revisionCreated)
        },
      })
    },
  })

  useRevisionMovedSubscription({
    variables,
    onSubscriptionData: ({ subscriptionData: { loading, data, error } }) => {
      if (!data || error || loading) {
        ErrorHandler.shouldNeverHappen(
          SUBSCRIPTIONS_SHOULD_BE_VALID('revisionMoved')
        )
        return
      }

      const { previousArtboardIdentifier } = data.revisionMoved
      const selectedRevisionChange =
        artboardIdentifier === previousArtboardIdentifier

      onRevisionChange({
        type: 'revision-moved',
        selectedRevisionChange,
        revision: data.revisionMoved.revision,
      })
    },
  })

  useRevisionDeletedSubscription({
    variables: { permanentArtboardShortId },
    onSubscriptionData: ({
      client,
      subscriptionData: { loading, data, error },
    }) => {
      if (!data || error || loading) {
        ErrorHandler.shouldNeverHappen(
          SUBSCRIPTIONS_SHOULD_BE_VALID('revisionDeleted')
        )
        return
      }
      const previousArtboardIdentifier = data.revisionDeleted.artboardIdentifier
      const selectedRevisionChange =
        artboardIdentifier === previousArtboardIdentifier

      onRevisionChange({
        type: 'revision-deleted',
        selectedRevisionChange,
        revision: data.revisionDeleted,
      })

      // Try to update activity feed, if the user went directly to the
      // inspector this is expected to fail
      updateActivityFeed({
        client,
        variables: {
          ...variables,
          shareIdentifier: share.identifier,
        },
        update: activityFeed => {
          const index = activityFeed.entries.findIndex(
            x => x.identifier === data.revisionDeleted.identifier
          )
          activityFeed.entries[index] = data.revisionDeleted
        },
      })
    },
    fetchPolicy: 'no-cache',
  })
}
